import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getManagerById } from '../Apis/ManagerAPIs';
import { getImageUrl } from '../Apis/api';
import { toast } from 'react-toastify';

const EmployeeViewLayer = () => {
    const { id } = useParams();
    const [adminData, setAdminData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchAdminDetails = async () => {
            if (id) {
                setLoading(true);
                const res = await getManagerById(id);
                if (res.status && res.response) {
                    setAdminData(res.response?.data || res.response);
                }
                setLoading(false);
            }
        };
        fetchAdminDetails();
    }, [id]);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary-600" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!adminData) {
        return (
            <div className="card h-100 p-24 text-center radius-12 border-0 shadow-sm bg-base">
                <h5 className="text-secondary-light">Admin details not found.</h5>
                <Link to="/employees" className="btn btn-secondary w-fit-content mx-auto mt-16 radius-8 d-flex align-items-center gap-1" style={{ backgroundColor: "#f7e7da", color: "black", border: "none", fontWeight: "600" }}>
                    <Icon icon="solar:arrow-left-bold" /> Back to List
                </Link>
            </div>
        );
    }

    const roleName = adminData.role?.roleName || adminData.roleName || "Manager";

    return (
        <div className="container-fluid px-0">
            {/* Header Section - Full Width */}
            <div className="card border-0 shadow-sm radius-16 mb-24 overflow-hidden">
                <div className="card-body p-20" style={{ background: "linear-gradient(135deg, #000000, #bb0007, #ed1c24)" }}>
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <div className="d-flex align-items-center gap-2">
                             <h6 className="text-white mb-0 fw-bold letter-spacing-05 text-sm"> PROFILE</h6>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <Link to={`/employees/edit/${id}`} className="btn btn-md btn-dark d-flex align-items-center gap-2 radius-8 px-20 fw-bold">
                                <Icon icon="solar:pen-new-square-bold" /> Edit Profile
                            </Link>
                            <Link to="/employees" className="btn btn-md d-flex align-items-center gap-2 radius-8 px-20" style={{ backgroundColor: "#f7e7da", color: "black", fontWeight: "600" }}>
                                <Icon icon="solar:arrow-left-bold" /> Back to List
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-3">
                {/* Profile Overview Card - Left Side */}
                <div className="col-lg-4">
                    <div className="card border-0 shadow-sm radius-16 overflow-hidden h-100" style={{ background: "#ffffff" }}>
                        {/* Profile Header - Compact Premium Gradient */}
                        <div className="profile-header-premium p-24 text-center position-relative">
                            <div className="position-relative d-inline-block mb-3">
                                <div className="avatar-wrapper">
                                    <div className="avatar-main rounded-circle border border-3 border-white shadow-lg bg-white overflow-hidden d-flex align-items-center justify-content-center mx-auto transition-all hover-scale">
                                        {adminData.profileImage ? (
                                            <img 
                                                src={getImageUrl(adminData.profileImage)} 
                                                alt="Profile" 
                                                className="w-100 h-100 object-fit-cover" 
                                            />
                                        ) : (
                                            <div className="w-100 h-100 rounded-circle bg-primary-gradient d-flex align-items-center justify-content-center">
                                                <span className="text-white fs-4 fw-bold">
                                                    {adminData.name ? adminData.name.charAt(0).toUpperCase() : 'A'}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="status-indicator-online shadow-sm"></div>
                                </div>
                            </div>

                            <h6 className="fw-bold mb-1 text-white tracking-tight text-sm">{adminData.name}</h6>
                            <span className="badge glass-badge px-10 py-3 radius-50 text-white border border-white-20" style={{ fontSize: '10px' }}>
                                {roleName}
                            </span>
                        </div>

                        {/* Quick Actions/Stats - Compact */}
                        <div className="p-20">
                            <div className="d-grid gap-2">
                                <div className="role-card p-16 radius-12 d-flex align-items-center gap-3 transition-all">
                                    <div className="icon-circle-md bg-primary-soft shadow-sm d-flex align-items-center justify-content-center">
                                        <Icon icon="solar:shield-star-bold-duotone" className="text-primary-600 fs-5" />
                                    </div>
                                    <div>
                                        <p className="text-secondary-light extra-small mb-0 fw-bold text-uppercase tracking-wider">Access Level</p>
                                        <p className="fw-bold mb-0 text-dark small">{roleName}</p>
                                    </div>
                                </div>
                                
                                <div className="status-card p-16 radius-12 d-flex align-items-center gap-3 transition-all">
                                    <div className="icon-circle-md bg-success-soft shadow-sm d-flex align-items-center justify-content-center">
                                        <Icon icon="solar:check-circle-bold-duotone" className="text-success-main fs-5" />
                                    </div>
                                    <div>
                                        <p className="text-secondary-light extra-small mb-0 fw-bold text-uppercase tracking-wider">Status</p>
                                        <p className="fw-bold mb-0 text-success-main small">Active</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Details Card - Right Side */}
                <div className="col-lg-8">
                    <div className="card border-0 shadow-sm radius-16 bg-white h-100 overflow-hidden">
                        <div className="card-header bg-white border-bottom border-neutral-100 p-20">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h6 className="fw-bold mb-0 text-dark tracking-tight">Personal Information</h6>
                                    <p className="text-secondary-light extra-small mb-0">Employee profile data</p>
                                </div>
                                <div className="icon-box-sm bg-neutral-50 radius-8 d-flex align-items-center justify-content-center border">
                                    <Icon icon="solar:user-id-bold-duotone" className="text-primary-600 fs-5" />
                                </div>
                            </div>
                        </div>

                        <div className="card-body p-20">
                            <div className="row g-3">
                                {/* Bento Grid Layout - Compact */}
                                {/* Full Name */}
                                <div className="col-12">
                                    <div className="bento-box p-20 radius-16 border border-neutral-100 bg-neutral-50 hover-bg-white transition-all">
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="icon-circle-md bg-white shadow-sm d-flex align-items-center justify-content-center">
                                                <Icon icon="solar:user-rounded-bold-duotone" className="text-primary-600 fs-4" />
                                            </div>
                                            <div>
                                                <p className="text-secondary-light extra-small mb-0 fw-bold text-uppercase tracking-wider">Full Name</p>
                                                <h6 className="fw-bold mb-0 text-dark text-sm">{adminData.name || "N/A"}</h6>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Email Address */}
                                <div className="col-md-6">
                                    <div className="bento-box p-20 radius-16 border border-neutral-100 bg-neutral-50 hover-bg-white transition-all h-100">
                                        <div className="icon-circle-sm bg-white shadow-sm mb-12 d-flex align-items-center justify-content-center">
                                            <Icon icon="solar:letter-bold-duotone" className="text-warning-main fs-5" />
                                        </div>
                                        <div>
                                            <p className="text-secondary-light extra-small mb-1 fw-bold text-uppercase tracking-wider">Email</p>
                                            <div className="d-flex align-items-center justify-content-between gap-2">
                                                <p className="fw-bold mb-0 text-dark text-break small">{adminData.email || "N/A"}</p>
                                                <button 
                                                    className="btn btn-icon-xs bg-white shadow-sm border radius-6"
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(adminData.email);
                                                        toast.success("Email copied");
                                                    }}
                                                >
                                                    <Icon icon="solar:copy-bold" className="text-secondary-light" style={{ fontSize: '14px' }} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Phone Number */}
                                <div className="col-md-6">
                                    <div className="bento-box p-20 radius-16 border border-neutral-100 bg-neutral-50 hover-bg-white transition-all h-100">
                                        <div className="icon-circle-sm bg-white shadow-sm mb-12 d-flex align-items-center justify-content-center">
                                            <Icon icon="solar:phone-bold-duotone" className="text-success-main fs-5" />
                                        </div>
                                        <div>
                                            <p className="text-secondary-light extra-small mb-1 fw-bold text-uppercase tracking-wider">Phone</p>
                                            <div className="d-flex align-items-center justify-content-between gap-2">
                                                <p className="fw-bold mb-0 text-dark small">{adminData.phoneNumber || adminData.mobile || "N/A"}</p>
                                                <button 
                                                    className="btn btn-icon-xs bg-white shadow-sm border radius-6"
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(adminData.phoneNumber || adminData.mobile);
                                                        toast.success("Phone copied");
                                                    }}
                                                >
                                                    <Icon icon="solar:copy-bold" className="text-secondary-light" style={{ fontSize: '14px' }} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
                
                :root {
                    --primary-color: #ed1c24;
                    --primary-gradient: linear-gradient(135deg, #000000 0%, #bb0007 50%, #ed1c24 100%);
                }

                .container-fluid {
                    font-family: 'Plus Jakarta Sans', sans-serif;
                }

                .extra-small { font-size: 10px; }
                .tracking-tight { letter-spacing: -0.01em; }
                .tracking-wider { letter-spacing: 0.03em; }
                
                .text-white-70 { color: rgba(255, 255, 255, 0.7); }
                
                .bg-primary-soft { background-color: rgba(237, 28, 36, 0.05); }
                .bg-success-soft { background-color: rgba(25, 135, 84, 0.05); }
                .bg-primary-gradient { background: var(--primary-gradient); }
                
                .profile-header-premium {
                    background: var(--primary-gradient);
                    position: relative;
                }
                
                .avatar-wrapper {
                    position: relative;
                    display: inline-block;
                }

                .avatar-main {
                    width: 100px;
                    height: 100px;
                    border: 4px solid rgba(255, 255, 255, 0.2) !important;
                }

                .status-indicator-online {
                    position: absolute;
                    bottom: 5px;
                    right: 5px;
                    width: 16px;
                    height: 16px;
                    background: #22c55e;
                    border: 3px solid #fff;
                    border-radius: 50%;
                }

                .glass-badge {
                    background: rgba(255, 255, 255, 0.1);
                    backdrop-filter: blur(4px);
                    font-weight: 600;
                    font-size: 11px;
                }

                .role-card, .status-card {
                    background: #f8fafc;
                    border: 1px solid #f1f5f9;
                }

                .icon-circle-sm { width: 32px; height: 32px; border-radius: 10px; }
                .icon-circle-md { width: 40px; height: 40px; border-radius: 12px; }
                .icon-box-sm { width: 36px; height: 36px; }

                .bento-box:hover {
                    background: #ffffff !important;
                    border-color: var(--primary-color) !important;
                    box-shadow: 0 10px 20px -5px rgba(0, 0, 0, 0.05);
                    transform: translateY(-2px);
                }

                .btn-icon-xs {
                    width: 26px;
                    height: 26px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s;
                }

                .btn-icon-xs:hover {
                    background: var(--primary-color) !important;
                    color: white !important;
                }
                .btn-icon-xs:hover svg {
                    color: white !important;
                }

                .transition-all {
                    transition: all 0.3s ease;
                }
                
                .hover-scale:hover { transform: scale(1.03); }
                .radius-12 { border-radius: 12px !important; }
                .radius-16 { border-radius: 16px !important; }
            `}</style>
        </div>
    );
};

export default EmployeeViewLayer;